/**
 * Support Service
 *
 * Handles support conversations API and WebSocket URL building.
 */

import api, { getAuthToken } from '../utils/api';
import config from '../config';

/**
 * List conversations (no messages)
 * GET /v1/api/support/conversations
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getConversations = async () => {
  try {
    const response = await api.get('/v1/api/support/conversations');

    // API may return array directly or { data: [] }
    const list = Array.isArray(response) ? response : (response?.data ?? []);
    if (Array.isArray(list)) {
      return {
        success: true,
        data: list,
        message: 'Conversations retrieved successfully',
      };
    }

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Conversations retrieved successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to retrieve conversations',
      data: null,
    };
  } catch (error) {
    console.error('Get conversations error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to retrieve conversations. Please try again.';
    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get one conversation with messages
 * GET /v1/api/support/conversations/:id
 *
 * Backend returns the conversation object at the top level (no wrapper):
 * { id, email, created_at, updated_at, last_message_at, messages: [{ id, conversation_id, sender, content, created_at, read_at }, ...] }
 * messages may be [] when there are no messages.
 *
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<{ success: boolean, data?: object, error?: string }>}
 */
export const getConversation = async (conversationId) => {
  try {
    const response = await api.get(`/v1/api/support/conversations/${conversationId}`);

    // Accept multiple response shapes
    let conversation = null;
    if (response && typeof response === 'object' && (response.id || response.email)) {
      // Backend returns raw conversation at top level: { id, email, messages, ... }
      conversation = response;
    } else if (response?.status === 'success' && response?.data) {
      conversation = response.data;
    } else if (response?.data && typeof response.data === 'object') {
      conversation = response.data;
    } else if (response?.conversation && typeof response.conversation === 'object') {
      conversation = response.conversation;
    }

    if (conversation) {
      return {
        success: true,
        data: conversation,
        message: response?.message || 'Conversation retrieved successfully',
      };
    }

    return {
      success: false,
      error: response?.message || response?.error || 'Failed to retrieve conversation',
      data: null,
    };
  } catch (error) {
    console.error('Get conversation error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to retrieve conversation. Please try again.';
    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Build WebSocket URL for admin support chat.
 * Pass raw JWT in token query (no "Bearer" prefix).
 * ws(s)://host/v1/api/support/ws?conversation_id=<id>&token=<ACCESS_TOKEN>
 * @param {string} conversationId - Conversation ID
 * @returns {string|null} WebSocket URL or null if no token
 */
export const getSupportWebSocketUrl = (conversationId) => {
  const token = getAuthToken();
  if (!token || !conversationId) return null;

  const base = config.api.baseURL.replace(/\/$/, '');
  const wsBase = base.startsWith('https') ? base.replace(/^https/, 'wss') : base.replace(/^http/, 'ws');
  const path = '/v1/api/support/ws';
  const params = new URLSearchParams({
    conversation_id: conversationId,
    token,
  });
  return `${wsBase}${path}?${params.toString()}`;
};

/**
 * Get support notification emails
 * GET /v1/api/support/notifications?skip=0&limit=50
 * @param {Object} params - { skip?: number, limit?: number }
 * @returns {Promise<{ success: boolean, data?: array, pagination?: object, error?: string }>}
 */
export const getNotifications = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.skip != null) query.set('skip', params.skip);
    if (params.limit != null) query.set('limit', params.limit);
    const queryString = query.toString();
    const endpoint = `/v1/api/support/notifications${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(endpoint);

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        pagination: response.pagination,
        message: response.message || 'Notification emails retrieved',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to retrieve notifications',
      data: null,
      pagination: null,
    };
  } catch (error) {
    console.error('Get notifications error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to retrieve notifications. Please try again.';
    return {
      success: false,
      error: errorMessage,
      data: null,
      pagination: null,
    };
  }
};

/**
 * Mark notification as spam or not
 * PATCH /v1/api/support/notifications/:notificationId/spam
 * @param {string} notificationId - Notification ID
 * @param {boolean} markedSpam - true to mark as spam, false to unmark
 * @returns {Promise<{ success: boolean, data?: object, error?: string }>}
 */
export const markNotificationSpam = async (notificationId, markedSpam = true) => {
  try {
    const response = await api.patch(`/v1/api/support/notifications/${notificationId}/spam`, {
      marked_spam: markedSpam,
    });

    if (response?.status === 'success' || response?.id) {
      return {
        success: true,
        data: response?.data ?? response,
        message: response?.message || (markedSpam ? 'Marked as spam' : 'Unmarked as spam'),
      };
    }

    return {
      success: false,
      error: response?.message || response?.error || 'Failed to update notification',
      data: null,
    };
  } catch (error) {
    console.error('Mark notification spam error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to update notification. Please try again.';
    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get contact form messages
 * GET /v1/api/support/contact-messages?skip=0&limit=50
 * @param {Object} params - { skip?: number, limit?: number }
 * @returns {Promise<{ success: boolean, data?: array, pagination?: object, error?: string }>}
 */
export const getContactMessages = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.skip != null) query.set('skip', params.skip);
    if (params.limit != null) query.set('limit', params.limit);
    const queryString = query.toString();
    const endpoint = `/v1/api/support/contact-messages${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(endpoint);

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        pagination: response.pagination,
        message: response.message || 'Contact messages retrieved',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to retrieve contact messages',
      data: null,
      pagination: null,
    };
  } catch (error) {
    console.error('Get contact messages error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to retrieve contact messages. Please try again.';
    return {
      success: false,
      error: errorMessage,
      data: null,
      pagination: null,
    };
  }
};

const supportService = {
  getConversations,
  getConversation,
  getSupportWebSocketUrl,
  getNotifications,
  markNotificationSpam,
  getContactMessages,
};
export default supportService;
